import { useMemo } from 'react';
import {
  CheckCircleOutline as DoneIcon,
  ErrorOutline as FailedIcon,
  RadioButtonUnchecked as IdleIcon,
  HourglassEmpty as PendingIcon,
  RemoveCircleOutline as SkippedIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Typography,
} from '@mui/material';
import { ExportProgressState, PageResult } from '../types';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: 640,
    maxWidth: '90vw',
  },
});

const StatusRow = styled(ListItem)({
  paddingTop: 4,
  paddingBottom: 4,
});

const formatElapsed = (ms: number | undefined): string => {
  if (!ms) {
    return '';
  }
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
};

const getIcon = (status: PageResult['status']) => {
  switch (status) {
    case 'pending':
      return <IdleIcon color="disabled" fontSize="small" />;
    case 'loading':
    case 'capturing':
      return <CircularProgress size={16} />;
    case 'done':
      return <DoneIcon color="success" fontSize="small" />;
    case 'skipped':
      return <SkippedIcon color="disabled" fontSize="small" />;
    case 'failed':
      return <FailedIcon color="error" fontSize="small" />;
    case 'canceled':
      return <PendingIcon color="disabled" fontSize="small" />;
    default:
      return <IdleIcon color="disabled" fontSize="small" />;
  }
};

const getSecondaryLabel = (page: PageResult): string => {
  switch (page.status) {
    case 'pending':
      return 'Waiting';
    case 'loading':
      return 'Loading page…';
    case 'capturing':
      return 'Capturing…';
    case 'done':
      return `Captured in ${formatElapsed((page.finishedAt ?? 0) - (page.startedAt ?? 0))}`;
    case 'skipped':
      return page.reason ? `Skipped — ${page.reason}` : 'Skipped';
    case 'failed':
      return page.reason ? `Failed — ${page.reason}` : 'Failed';
    case 'canceled':
      return 'Canceled';
    default:
      return '';
  }
};

export interface PdfExportProgressDialogProps {
  open: boolean;
  state: ExportProgressState;
  onCancel: () => void;
  onClose: () => void;
  onDownloadAgain: () => void;
}

export const PdfExportProgressDialog = ({
  open,
  state,
  onCancel,
  onClose,
  onDownloadAgain,
}: PdfExportProgressDialogProps) => {
  const total = state.pages.length;
  const settled = useMemo(
    () => state.pages.filter(p => ['done', 'skipped', 'failed', 'canceled'].includes(p.status)).length,
    [state.pages]
  );
  const percent = total === 0 ? 0 : Math.round((settled / total) * 100);

  const isRunning = state.status === 'running' || state.status === 'rendering';
  const isDone = state.status === 'done';
  const isCanceled = state.status === 'canceled';
  const isFailed = state.status === 'failed';
  let elapsed = 0;
  if (state.finishedAt && state.startedAt) {
    elapsed = state.finishedAt - state.startedAt;
  } else if (state.startedAt) {
    elapsed = performance.now() - state.startedAt;
  }

  return (
    <StyledDialog
      open={open}
      onClose={isRunning ? undefined : onClose}
      disableEscapeKeyDown={isRunning}
      id="pdf-export-dialog"
    >
      <DialogTitle>PDF Export</DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2">
              {isDone && 'Export complete'}
              {isCanceled && 'Export canceled'}
              {isFailed && 'Export failed'}
              {state.status === 'running' && `Capturing ${settled} of ${total}`}
              {state.status === 'rendering' && 'Assembling PDF…'}
              {state.status === 'idle' && 'Preparing…'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatElapsed(elapsed)} · {percent}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={percent} />
        </Box>

        <List dense id="pdf-export-dialog__pages">
          {state.pages.map(page => (
            <StatusRow key={`${page.index}-${page.exportTarget}`} id={`pdf-export-dialog__page-${page.exportTarget}`}>
              <ListItemIcon sx={{ minWidth: 32 }}>{getIcon(page.status)}</ListItemIcon>
              <ListItemText
                primary={page.title}
                secondary={getSecondaryLabel(page)}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </StatusRow>
          ))}
        </List>

        {isFailed && state.error && (
          <Typography color="error" variant="caption">
            {state.error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {isRunning && (
          <Button onClick={onCancel} id="pdf-export-dialog__cancel">
            Cancel
          </Button>
        )}
        {!isRunning && isDone && state.resultBlob && (
          <Button onClick={onDownloadAgain} id="pdf-export-dialog__download-again">
            Download again
          </Button>
        )}
        {!isRunning && (
          <Button onClick={onClose} variant="contained" id="pdf-export-dialog__close">
            Close
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default PdfExportProgressDialog;
